import { useState } from "react";
import type {
  EffortLevel,
  EnterpriseAdminBundleRawModel,
  ModelParamsByChain,
  PiChainParams,
  PiReasoningEffort,
  SdkChainParams,
  ThinkingMode,
} from "@dosia/shared";
import { sortByThinkingLevel, THINKING_LEVELS } from "@dosia/shared";
import { cn } from "../lib/cn.js";

/**
 * 可复用 per-chain 模型参数范围编辑器（模型参数范围治理 Model Z，PR-2，2026-07-23）。
 *
 * 红线 #9：**一个**范围编辑器组件，组织页（ModelManagementPanel）+ 超管治理页（PR-3
 * PlatformModelGovernance）共用，别造两份。语义完全由入参 `value` + `onChange` 驱动，组件
 * 不知道自己在哪层（org / role / 平台）——上层只管把该 base 的 `ModelParamsByChain` 传进来、
 * 拿回下一个（或 undefined = 清空回落继承）。
 *
 * **能力天花板 per-chain 协议专属**（方案 §2，Codex 修 #6）：
 *  - SDK 段（Claude 主脑用法）能力取 **anthropic 协议行** caps（`sdkCaps`）：thinking + effort。
 *  - Pi 段（母语引擎）能力取 **openai 协议行** caps（`piCaps`）：effort + outputTokenParam。
 *    Pi 段仅 `piEligible === true` 且该链有可编辑能力时才出（能力驱动，无能力不出控件）。
 *
 * **三态语义**（方案 §2，禁空数组哨兵）：某 enum 参数
 *  - 不勾任何档 → `allowedSubset[param]` absent = 继承上层（全部天花板档可选）。
 *  - 勾 1 档 → 锁定（员工不可选 = 强制该档）。
 *  - 勾 ≥2 档 → 可选子集（员工在其中选）。
 *  `paramDefaults[param]` 留空 = 无默认（跟随网关）；设了 = ★ 标默认（须在生效允许集内）。
 */

type ModelCaps = EnterpriseAdminBundleRawModel["capabilities"];

/** SDK thinking 词表天花板（wire 词表 auto/adaptive/enabled/disabled）——thinkingParam.supported
 *  为门，canDisable===false 时剔除 disabled（always-on 模型不可关思考）。缺 supported → []。 */
export function thinkingModesFor(caps: ModelCaps): ThinkingMode[] {
  if (caps?.thinkingParam?.supported !== true) return [];
  const all: ThinkingMode[] = ["auto", "adaptive", "enabled", "disabled"];
  return caps.thinkingParam.canDisable === false ? all.filter((m) => m !== "disabled") : all;
}

/** SDK 主脑池保守兜底档位（4 档，**无 xhigh** —— xhigh 永不从兜底推断，只在模型显式声明时提供）。
 *  镜像 model-registry ALL_EFFORT_LEVELS。 */
const ALL_SDK_EFFORT_LEVELS: readonly EffortLevel[] = ["low", "medium", "high", "max"];

const isEffortLevel = (l: unknown): l is EffortLevel =>
  l === "low" || l === "medium" || l === "high" || l === "xhigh" || l === "max";

/** SDK 段 effort 天花板 —— 镜像服务端 resolvePerChainParams(sdk 分支)：声明 supportedEffortLevels
 *  用其全集（**保留 medium/xhigh**）；仅 supportsEffort 无具体档位 → 保守 ALL_SDK_EFFORT_LEVELS（4 档，
 *  无 xhigh）；都无 → []。 */
export function sdkEffortLevelsFor(caps: ModelCaps): EffortLevel[] {
  const levels = (caps?.supportedEffortLevels ?? []).filter(isEffortLevel);
  // canonical 呈现序（红线#9 单源 sortByThinkingLevel）：xhigh 排到 high↔max 间，不随声明顺序。
  if (levels.length > 0) return sortByThinkingLevel(levels);
  return caps?.supportsEffort === true ? [...ALL_SDK_EFFORT_LEVELS] : [];
}

/** Pi 段 effort 天花板 —— 镜像服务端 resolvePerChainParams(pi 分支)：保留 THINKING_LEVELS
 *  里的档；仅 supportsEffort 无具体档位 → 六档全集；都无 → []。 */
export function effortLevelsFor(caps: ModelCaps): PiReasoningEffort[] {
  const declared = caps?.supportedEffortLevels ?? [];
  if (declared.length > 0) return sortByThinkingLevel(declared);
  return caps?.supportsEffort === true ? [...THINKING_LEVELS] : [];
}

// ── 纯写入 helper（value → next，逐层清理空对象回落继承） ────────────────────────

/** 去掉 undefined 值的键 → 空对象返回 undefined（spread-with-undefined 会留 `{k:undefined}`，
 *  必须 compact 后再判空，否则回落继承失效）。 */
function compact<T extends object>(obj: T | undefined): T | undefined {
  if (!obj) return undefined;
  const out = Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
  return Object.keys(out).length > 0 ? out : undefined;
}
function pruneSdk(sdk: SdkChainParams | undefined): SdkChainParams | undefined {
  if (!sdk) return undefined;
  const pd = compact(sdk.paramDefaults);
  const as = compact(sdk.allowedSubset);
  if (!pd && !as) return undefined;
  return { ...(pd ? { paramDefaults: pd } : {}), ...(as ? { allowedSubset: as } : {}) };
}
function prunePi(pi: PiChainParams | undefined): PiChainParams | undefined {
  if (!pi) return undefined;
  const pd = compact(pi.paramDefaults);
  const as = compact(pi.allowedSubset);
  if (!pd && !as) return undefined;
  return { ...(pd ? { paramDefaults: pd } : {}), ...(as ? { allowedSubset: as } : {}) };
}
/** 组装 + 全清 → undefined（回落继承，上层据此删 map key）。 */
function assemble(sdk: SdkChainParams | undefined, pi: PiChainParams | undefined): ModelParamsByChain | undefined {
  const s = pruneSdk(sdk);
  const p = prunePi(pi);
  if (!s && !p) return undefined;
  return { ...(s ? { sdk: s } : {}), ...(p ? { pi: p } : {}) };
}

/** enum allowedSubset toggle：按天花板顺序保序增删；删到空 → 该 param 回落继承；删掉的值若正是
 *  默认 → 同步清默认（越界默认无意义）。返回下一个 chain params（sdk 或 pi 的 effort/thinking 侧）。 */
function toggleAllowed<V extends string>(
  current: V[] | undefined,
  value: V,
  ceiling: readonly V[],
): { next: V[] | undefined; removed: boolean } {
  const set = new Set(current ?? []);
  let removed = false;
  if (set.has(value)) {
    set.delete(value);
    removed = true;
  } else {
    set.add(value);
  }
  const next = ceiling.filter((c) => set.has(c));
  return { next: next.length > 0 ? next : undefined, removed };
}

// ── 子渲染：单个 enum 参数（chips 允许档 + ★默认） ──────────────────────────────

function EnumParamRow<V extends string>({
  base,
  chain,
  param,
  label,
  ceiling,
  allowed,
  defaultValue,
  onToggleAllowed,
  onSetDefault,
}: {
  base: string;
  chain: "sdk" | "pi";
  param: string;
  label: string;
  ceiling: readonly V[];
  allowed: V[] | undefined;
  defaultValue: V | undefined;
  onToggleAllowed: (value: V) => void;
  onSetDefault: (value: V) => void;
}): JSX.Element | null {
  if (ceiling.length === 0) return null;
  const allowedSet = new Set(allowed ?? []);
  const configured = (allowed?.length ?? 0) > 0;
  // 默认可选面：未收窄(继承)→全部天花板；已收窄→仅允许集内（越界 ★ 禁用）。
  const canBeDefault = (v: V): boolean => !configured || allowedSet.has(v);
  const stateHint = !configured
    ? "继承上层（全部档位可选）"
    : allowed!.length === 1
      ? `锁定：${allowed![0]}`
      : `可选子集：${allowed!.join(" / ")}`;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-dosia-text-secondary">{label}</span>
      <div className="flex flex-wrap items-center gap-1.5">
        {ceiling.map((v) => {
          const on = allowedSet.has(v);
          const isDefault = defaultValue === v;
          return (
            <span
              key={v}
              className={cn(
                "inline-flex items-center gap-1 rounded border px-1.5 py-0.5",
                on ? "border-dosia-accent bg-dosia-accent/10" : "border-dosia-border bg-dosia-bg-light",
              )}
            >
              <button
                type="button"
                onClick={() => onToggleAllowed(v)}
                aria-pressed={on}
                aria-label={`allow-${chain}-${param}-${base}-${v}`}
                className={cn("text-xs", on ? "text-dosia-text font-medium" : "text-dosia-text-dim")}
              >
                {v}
              </button>
              <button
                type="button"
                onClick={() => canBeDefault(v) && onSetDefault(v)}
                disabled={!canBeDefault(v)}
                aria-pressed={isDefault}
                aria-label={`default-${chain}-${param}-${base}-${v}`}
                title={isDefault ? "默认（点击取消）" : "设为默认"}
                className={cn(
                  "text-xs leading-none",
                  isDefault ? "text-amber-500" : canBeDefault(v) ? "text-dosia-text-dim hover:text-amber-500" : "text-dosia-border",
                )}
              >
                {isDefault ? "★" : "☆"}
              </button>
            </span>
          );
        })}
      </div>
      <span className="text-dosia-text-dim">
        {stateHint} · 默认：{defaultValue ?? "跟随网关"}
      </span>
    </div>
  );
}

export function ChainParamRangeEditor({
  base,
  piEligible,
  sdkCaps,
  piCaps,
  value,
  onChange,
}: {
  base: string;
  piEligible: boolean;
  sdkCaps: ModelCaps;
  piCaps: ModelCaps;
  value: ModelParamsByChain | undefined;
  onChange: (base: string, next: ModelParamsByChain | undefined) => void;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);

  const sdkThinking = thinkingModesFor(sdkCaps);
  const sdkEffort = sdkEffortLevelsFor(sdkCaps);
  const piEffort = effortLevelsFor(piCaps);
  const piOutputMax = piCaps?.outputTokenParam?.max;
  const showSdk = sdkThinking.length > 0 || sdkEffort.length > 0;
  const showPi = piEligible && (piEffort.length > 0 || typeof piOutputMax === "number" || !!piCaps?.outputTokenParam);
  if (!showSdk && !showPi) return null;

  const sdk = value?.sdk;
  const pi = value?.pi;
  const hasOverride = !!assemble(sdk, pi);

  // ── SDK 段写入 ──
  const setSdkAllowedEffort = (v: EffortLevel): void => {
    const { next, removed } = toggleAllowed(sdk?.allowedSubset?.effort, v, sdkEffort);
    const nextSdk: SdkChainParams = {
      paramDefaults:
        removed && sdk?.paramDefaults?.effort === v
          ? { ...sdk.paramDefaults, effort: undefined }
          : sdk?.paramDefaults,
      allowedSubset: { ...sdk?.allowedSubset, effort: next },
    };
    onChange(base, assemble(nextSdk, pi));
  };
  const setSdkDefaultEffort = (v: EffortLevel): void => {
    const cur = sdk?.paramDefaults?.effort;
    onChange(base, assemble({ ...sdk, paramDefaults: { ...sdk?.paramDefaults, effort: cur === v ? undefined : v } }, pi));
  };
  const setSdkAllowedThinking = (v: ThinkingMode): void => {
    const { next, removed } = toggleAllowed(sdk?.allowedSubset?.thinking, v, sdkThinking);
    const nextSdk: SdkChainParams = {
      paramDefaults:
        removed && sdk?.paramDefaults?.thinking === v
          ? { ...sdk.paramDefaults, thinking: undefined }
          : sdk?.paramDefaults,
      allowedSubset: { ...sdk?.allowedSubset, thinking: next },
    };
    onChange(base, assemble(nextSdk, pi));
  };
  const setSdkDefaultThinking = (v: ThinkingMode): void => {
    const cur = sdk?.paramDefaults?.thinking;
    onChange(base, assemble({ ...sdk, paramDefaults: { ...sdk?.paramDefaults, thinking: cur === v ? undefined : v } }, pi));
  };

  // ── Pi 段写入 ──
  const setPiAllowedEffort = (v: PiReasoningEffort): void => {
    const { next, removed } = toggleAllowed(pi?.allowedSubset?.effort, v, piEffort);
    const nextPi: PiChainParams = {
      paramDefaults:
        removed && pi?.paramDefaults?.effort === v
          ? { ...pi.paramDefaults, effort: undefined }
          : pi?.paramDefaults,
      allowedSubset: { ...pi?.allowedSubset, effort: next },
    };
    onChange(base, assemble(sdk, nextPi));
  };
  const setPiDefaultEffort = (v: PiReasoningEffort): void => {
    const cur = pi?.paramDefaults?.effort;
    onChange(base, assemble(sdk, { ...pi, paramDefaults: { ...pi?.paramDefaults, effort: cur === v ? undefined : v } }));
  };
  /** 夹到网关 outputTokenParam.max（Codex 修 #1）；空/非法 → undefined（回落继承）。 */
  const clampOutput = (raw: string): number | undefined => {
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return typeof piOutputMax === "number" ? Math.min(n, piOutputMax) : n;
  };
  const setPiOutputMax = (raw: string): void => {
    const max = clampOutput(raw);
    const nextAllowed = { ...pi?.allowedSubset, outputTokens: max !== undefined ? { max } : undefined };
    // 默认若超过新上限 → 钳回。
    const curDefault = pi?.paramDefaults?.outputTokens;
    const clampedDefault = max !== undefined && curDefault !== undefined ? Math.min(curDefault, max) : curDefault;
    onChange(base, assemble(sdk, {
      paramDefaults: { ...pi?.paramDefaults, outputTokens: clampedDefault },
      allowedSubset: nextAllowed,
    }));
  };
  const setPiOutputDefault = (raw: string): void => {
    let d = clampOutput(raw);
    const effectiveMax = pi?.allowedSubset?.outputTokens?.max ?? piOutputMax;
    if (d !== undefined && typeof effectiveMax === "number") d = Math.min(d, effectiveMax);
    onChange(base, assemble(sdk, { ...pi, paramDefaults: { ...pi?.paramDefaults, outputTokens: d } }));
  };

  return (
    <div className="ml-6 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-xs font-medium text-dosia-accent hover:underline"
          aria-label={`toggle-params-${base}`}
        >
          {open ? "收起参数 ▴" : "调整范围 / 默认 ▾"}
        </button>
        {hasOverride ? <span className="text-xs text-dosia-text-dim">已覆盖参数</span> : null}
      </div>
      {open ? (
        <div className="flex flex-col gap-3 rounded border border-dosia-border bg-dosia-bg-light px-3 py-2 text-xs">
          {showSdk ? (
            <div className="flex flex-col gap-2">
              <span className="font-medium text-dosia-text">主脑用法（SDK）</span>
              <EnumParamRow
                base={base}
                chain="sdk"
                param="thinking"
                label="思考档位（thinking）"
                ceiling={sdkThinking}
                allowed={sdk?.allowedSubset?.thinking}
                defaultValue={sdk?.paramDefaults?.thinking}
                onToggleAllowed={setSdkAllowedThinking}
                onSetDefault={setSdkDefaultThinking}
              />
              <EnumParamRow
                base={base}
                chain="sdk"
                param="effort"
                label="推理档位（effort）"
                ceiling={sdkEffort}
                allowed={sdk?.allowedSubset?.effort}
                defaultValue={sdk?.paramDefaults?.effort}
                onToggleAllowed={setSdkAllowedEffort}
                onSetDefault={setSdkDefaultEffort}
              />
            </div>
          ) : null}
          {showPi ? (
            <div className="flex flex-col gap-2 border-t border-dosia-border/60 pt-2">
              <span className="font-medium text-dosia-text">母语引擎（Pi）</span>
              <EnumParamRow
                base={base}
                chain="pi"
                param="effort"
                label="推理档位（effort）"
                ceiling={piEffort}
                allowed={pi?.allowedSubset?.effort}
                defaultValue={pi?.paramDefaults?.effort}
                onToggleAllowed={setPiAllowedEffort}
                onSetDefault={setPiDefaultEffort}
              />
              <div className="flex flex-col gap-1">
                <span className="text-dosia-text-secondary">
                  输出上限（outputTokens）{typeof piOutputMax === "number" ? ` · 网关上界 ${piOutputMax}` : ""}
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5">
                    <span className="text-dosia-text-dim">允许上限</span>
                    <input
                      type="number"
                      min={1}
                      max={piOutputMax}
                      value={pi?.allowedSubset?.outputTokens?.max ?? ""}
                      onChange={(e) => setPiOutputMax(e.target.value)}
                      aria-label={`pi-output-max-${base}`}
                      placeholder={typeof piOutputMax === "number" ? `继承 ${piOutputMax}` : "继承"}
                      className="w-28 rounded border border-dosia-border bg-dosia-bg px-1.5 py-0.5"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-dosia-text-dim">默认输出</span>
                    <input
                      type="number"
                      min={1}
                      max={pi?.allowedSubset?.outputTokens?.max ?? piOutputMax}
                      value={pi?.paramDefaults?.outputTokens ?? ""}
                      onChange={(e) => setPiOutputDefault(e.target.value)}
                      aria-label={`pi-output-default-${base}`}
                      placeholder="跟随网关"
                      className="w-28 rounded border border-dosia-border bg-dosia-bg px-1.5 py-0.5"
                    />
                  </label>
                </div>
              </div>
            </div>
          ) : null}
          <p className="text-dosia-text-dim">
            范围来自网关能力（只读天花板，随升级自动变）；勾档 = 收窄员工可选集（勾 1 = 锁定 / 勾 ≥2 = 子集 / 不勾 = 继承上层）；
            ★ = 默认值（留空 = 跟随网关）。
          </p>
        </div>
      ) : null}
    </div>
  );
}
